import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { PersonIcon } from '@radix-ui/react-icons';
import { Avatar } from '@producer/ui-kit';

import { Member, Project } from '../../app/entities';
import { SingleSelectDropdown } from '../select-dropdown';
import { context } from '../../app/context';
import { TaskPropertyButton } from '../../features/tasks/components/task-property.button';

import styles from './assignee-selector.module.scss';

type AssigneeSelectorProps = {
  project?: Project;
  onChange: (value?: string) => void;
  selectedMemberId?: string | undefined | null;
  iconOnly?: boolean;
  isViewOnly?: boolean;
};

export const AssigneeSelector: React.FC<AssigneeSelectorProps> = observer(
  ({ project, onChange, selectedMemberId, iconOnly, isViewOnly }) => {
    const space = context.space;

    const otherMembers = useMemo(
      () =>
        space.members
          .filter((member) => !project?.collaborators.includes(member))
          .sort((a, b) => a.fullName.localeCompare(b.fullName)),
      [space.members, project?.collaborators],
    );

    const memberOptions: React.ComponentProps<typeof SingleSelectDropdown>['options'] =
      project?.collaborators.map((el) => ({
        label: el.fullName,
        value: el._id,
        icon: <Avatar size="small" initials={el.initials} src={el.avatar} />,
      })) || [];

    const spaceMemberOptions: React.ComponentProps<typeof SingleSelectDropdown>['options'] =
      otherMembers?.map((el) => ({
        label: el.fullName,
        value: el._id,
        icon: <Avatar size="small" initials={el.initials} src={el.avatar} />,
      })) || [];

    const options: React.ComponentProps<typeof SingleSelectDropdown>['options'] = [
      {
        label: 'No assignee',
        value: '',
        icon: (
          <div className={styles['placeholder']}>
            <PersonIcon />
          </div>
        ),
      },
      ...memberOptions,
    ];

    const groups: React.ComponentProps<typeof SingleSelectDropdown>['groups'] = [
      {
        items: options,
      },
      ...(spaceMemberOptions?.length
        ? [
            {
              title: 'People not yet in project',
              items: spaceMemberOptions,
            },
          ]
        : []),
    ];

    const handleStepChange = (value?: string) => {
      // @ts-ignore
      onChange(value || null); // to allow unassign assignee
    };

    const assignee = selectedMemberId && (Member.getOne(selectedMemberId) as Member);

    const triggerContent = (label?: string) => {
      if (iconOnly) {
        return assignee ? (
          <div className={styles['custom-avatar']}>
            <Avatar size="small" initials={assignee.initials} src={assignee.avatar} />
          </div>
        ) : (
          <div className={styles['placeholder']} data-is-not-option>
            <PersonIcon />
          </div>
        );
      }

      return assignee ? (
        <TaskPropertyButton
          icon={<Avatar size="xsmall" initials={assignee.initials} src={assignee.avatar} />}
          label={label}
          isActive
        />
      ) : (
        <TaskPropertyButton icon={<PersonIcon />} label="No assignee" />
      );
    };

    return (
      <div className={styles['container']} data-icon-only={iconOnly} data-disabled={isViewOnly}>
        <SingleSelectDropdown
          trigger={(label) => (
            <div onClick={(e) => e.stopPropagation()}>
              <div
                className={styles['trigger']}
                data-icon-only={iconOnly}
                data-placeholder={!label}
                data-disabled={isViewOnly}
                tabIndex={0}
              >
                {triggerContent(label)}
              </div>
            </div>
          )}
          title="Assignee"
          placeholder={'Assign to...'}
          value={selectedMemberId || ''}
          groups={groups}
          onValueChange={handleStepChange}
          disabled={isViewOnly}
        />
      </div>
    );
  },
);
